import * as ENGINE from '@gnsx/genesys.js';

import type { PropertyMetadata } from '@gnsx/genesys.js';

import { getUnifiedPropertyMetadata } from '@/editor/core/editorClassMeta';
import { displayCase, getComponentDisplayName } from '@/editor/core/nameHelpers';
import { assetsStore } from '@/editor/store';

const ASSET_PATH_TYPES = new Set([
  'audioPath',
  'hdriPath',
  'jsclassPath',
  'jsonPath',
  'materialPath',
  'modelPath',
  'prefabPath',
  'resource',
  'scenePath',
  'sourcecodePath',
  'texturePath',
  'vfxPath',
  'videoPath',
  'voxelPath',
]);

/** One missing-asset issue on a target (actor or scene component). */
export type MissingAssetWarning = {
  dedupeKey: string;
  propertyPath: string;
  propertyDisplay: string;
  assetFileLabel: string;
  assetFolderPath: string;
  /** Set when the warning was collected from a scene component (outliner actor roll-up). */
  componentDisplayName?: string;
};

/**
 * Splits a normalised asset path into file name and parent folder path for UI copy.
 */
export function splitAssetPathForDisplay(normalisedPath: string): { fileName: string; folderPath: string } {
  const trimmed = normalisedPath.trim();
  const idx = trimmed.lastIndexOf('/');
  if (idx === -1) {
    return { fileName: trimmed, folderPath: '' };
  }
  return {
    fileName: trimmed.slice(idx + 1),
    folderPath: trimmed.slice(0, idx),
  };
}

/**
 * Renders a single line for tree tooltips. When `includeComponent` is true (outliner actor row),
 * prefixes with the scene component display name.
 */
export function formatMissingAssetWarningLine(w: MissingAssetWarning, opts?: { includeComponent?: boolean }): string {
  const loc = w.assetFolderPath ? `${w.assetFileLabel} (${w.assetFolderPath})` : w.assetFileLabel;
  const prefix = opts?.includeComponent && w.componentDisplayName ? `${w.componentDisplayName} — ` : '';
  return `${prefix}${w.propertyDisplay}: ${loc}`;
}

/**
 * Returns missing-asset warnings for one actor or scene component.
 */
export function getMissingAssetWarningItemsForTarget(target: ENGINE.Actor | ENGINE.SceneComponent | undefined): MissingAssetWarning[] {
  if (!target) return [];

  const metadata = getUnifiedPropertyMetadata(target);
  const warnings = new Map<string, MissingAssetWarning>();
  const componentDisplayName =
    target instanceof ENGINE.SceneComponent ? getComponentDisplayName(target, false, false) : undefined;

  for (const [propertyPath, propertyMeta] of Object.entries(metadata)) {
    const value = readByPath(target, propertyPath);
    collectWarningsForValue(value, propertyMeta, propertyPath, warnings, metadata, target.uuid, componentDisplayName);
  }

  return sortWarningItems(warnings);
}

/**
 * Returns missing-asset warnings for an actor and all its scene components.
 */
export function getMissingAssetWarningItemsForActor(actor: ENGINE.Actor | undefined): MissingAssetWarning[] {
  if (!actor) return [];

  const warnings = new Map<string, MissingAssetWarning>();
  mergeTargetWarnings(actor, warnings, undefined);

  const components = actor.getComponents(ENGINE.SceneComponent);
  for (const component of components) {
    mergeTargetWarnings(component, warnings, getComponentDisplayName(component, false, false));
  }

  return sortWarningItems(warnings);
}

function mergeTargetWarnings(
  target: ENGINE.Actor | ENGINE.SceneComponent,
  warnings: Map<string, MissingAssetWarning>,
  componentDisplayName: string | undefined,
) {
  const metadata = getUnifiedPropertyMetadata(target);
  for (const [propertyPath, propertyMeta] of Object.entries(metadata)) {
    const value = readByPath(target, propertyPath);
    collectWarningsForValue(value, propertyMeta, propertyPath, warnings, metadata, target.uuid, componentDisplayName);
  }
}

function sortWarningItems(warnings: Map<string, MissingAssetWarning>): MissingAssetWarning[] {
  return Array.from(warnings.values()).sort((a, b) => {
    const c = (a.componentDisplayName ?? '').localeCompare(b.componentDisplayName ?? '');
    if (c !== 0) return c;
    return a.propertyDisplay.localeCompare(b.propertyDisplay) || a.assetFileLabel.localeCompare(b.assetFileLabel);
  });
}

function collectWarningsForValue(
  value: unknown,
  meta: PropertyMetadata | undefined,
  propertyPath: string,
  warnings: Map<string, MissingAssetWarning>,
  rootMeta: Record<string, PropertyMetadata>,
  targetUuid: string,
  componentDisplayName: string | undefined,
) {
  if (!meta) return;

  const type = meta.type;
  if (type && ASSET_PATH_TYPES.has(type)) {
    if (typeof value === 'string') {
      const normalisedValue = normaliseAssetPath(value);
      if (normalisedValue && !assetsStore.assetTree.findFile(normalisedValue)) {
        const dedupeKey = `${targetUuid}|${propertyPath}|${normalisedValue}`;
        const { fileName, folderPath } = splitAssetPathForDisplay(normalisedValue);
        warnings.set(dedupeKey, {
          dedupeKey,
          propertyPath,
          propertyDisplay: humanizePropertyPath(rootMeta, propertyPath),
          assetFileLabel: fileName,
          assetFolderPath: folderPath,
          componentDisplayName,
        });
      }
    }
    return;
  }

  if (meta.type === 'object' && 'fields' in meta && meta.fields && value && typeof value === 'object') {
    for (const [fieldName, fieldMeta] of Object.entries(meta.fields)) {
      const nestedValue = readByPath(value, fieldName);
      collectWarningsForValue(
        nestedValue,
        fieldMeta,
        `${propertyPath}.${fieldName}`,
        warnings,
        rootMeta,
        targetUuid,
        componentDisplayName,
      );
    }
    return;
  }

  if (meta.type === 'array' && 'child' in meta && meta.child && Array.isArray(value)) {
    value.forEach((item, index) => {
      collectWarningsForValue(
        item,
        meta.child,
        `${propertyPath}[${index}]`,
        warnings,
        rootMeta,
        targetUuid,
        componentDisplayName,
      );
    });
  }
}

function normaliseAssetPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('/@') ? trimmed.slice(1) : trimmed;
}

function readByPath(source: unknown, propertyPath: string): unknown {
  if (!source || !propertyPath) return undefined;
  const pathParts = propertyPath.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);

  let current: unknown = source;
  for (const part of pathParts) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Tokenises a property path like `layers[0].texturePath` into string and index segments.
 */
export function tokenizePropertyPath(path: string): (string | number)[] {
  const parts: (string | number)[] = [];
  const re = /[^.[\]]+|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) parts.push(parseInt(m[1], 10));
    else parts.push(m[0]);
  }
  return parts;
}

/**
 * Builds a human-readable property label (display case, nested paths, array indices).
 */
export function humanizePropertyPath(rootMeta: Record<string, PropertyMetadata>, path: string): string {
  const segments = tokenizePropertyPath(path);
  const displayParts: string[] = [];
  let fields: Record<string, PropertyMetadata> | undefined = rootMeta;
  let pendingArrayElement: PropertyMetadata | undefined;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;

    if (typeof seg === 'number') {
      const last = displayParts.pop();
      displayParts.push(last ? `${last} [${seg + 1}]` : `[${seg + 1}]`);
      if (pendingArrayElement?.type === 'object' && 'fields' in pendingArrayElement && pendingArrayElement.fields) {
        fields = pendingArrayElement.fields;
      } else {
        fields = undefined;
      }
      pendingArrayElement = undefined;
      continue;
    }

    if (!fields) {
      displayParts.push(displayCase(seg));
      pendingArrayElement = undefined;
      continue;
    }

    const fieldMeta = fields[seg];
    displayParts.push(displayCase(seg));

    if (fieldMeta?.type === 'object' && 'fields' in fieldMeta && fieldMeta.fields) {
      fields = fieldMeta.fields;
      pendingArrayElement = undefined;
    } else if (fieldMeta?.type === 'array' && 'child' in fieldMeta && fieldMeta.child) {
      fields = undefined;
      pendingArrayElement = fieldMeta.child;
    } else {
      fields = undefined;
      pendingArrayElement = undefined;
    }
  }

  return displayParts.join(' → ');
}
